import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import * as _moment from 'moment';
import { AnalyticsService } from '@pulse-front/core';
import { ExportConfirmDialogComponent } from './export-confirm-dialog/export-confirm-dialog.component';
const moment = _moment;

interface ChartData {
  axisX: string[];
  axisY: number[];
}

interface ErrorItem {
  name: string;
  counter: number;
  axisX: string[];
  axisY: number[];
}

@Component({
  selector: 'panel-b2b-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss'],
})
export class AnalyticsComponent implements OnInit {

  public readonly Math = Math;

  clientsList: any[] = [];
  categoriesList: any[] = [];
  subcategoriesList: any[] = [];
  countriesList: any[] = [];
  productsList: any[] = [];

  rawCategories: any[] = [];
  rawSubcategories: any[] = [];
  rawProducts: any[] = [];

  filteredCategories: any[] = [];
  filteredSubcategories: any[] = [];
  filteredProducts: any[] = [];

  filterForm = new FormGroup({
    client: new FormControl(''),
    category: new FormControl(''),
    subcategory: new FormControl(''),
    country: new FormControl(''),
    product: new FormControl(''),
    rangeDate: new FormControl(null as Date[] | null),
  });

  /** Cliente y rango completo (inicio y fin) requeridos para Buscar y exportar Excel. */
  get canSubmitFilters(): boolean {
    const v = this.filterForm.value;
    if (!v.client) {
      return false;
    }
    const range = v.rangeDate as Date[] | null | undefined;
    return !!(range && range[0] && range[1]);
  }

  successTotal: number = 0;
  successTrend: string = '0%';

  successPathArea: string = ''; // El relleno degradado
  successPathLine: string = ''; // La línea sólida
  successPoints: any[] = [];    // Los círculos en los picos
  successLabels: string[] = []; // Etiquetas eje X

  errorTotal: number = 0;
  errorTrend: string = '0%';

  errorBars: any[] = [];
  errorLabels: string[] = [];

  isLoadingMetrics = false;

  barChartData = [{ x: 20, h1: 40, h2: 20 }, { x: 60, h1: 80, h2: 10 }];
  daysLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  dateLabels = ['01 Oct', '05 Oct', '10 Oct', '15 Oct', '20 Oct', 'Hoy'];
  isDownloadingReport = false;

  /** Mensaje bajo los botones del footer cuando faltan cliente o rango completo. */
  filtersFooterAlert: string | null = null;

  private readonly searchFiltersIncompleteMsg =
    'Selecciona un cliente y define el rango de fechas (inicio y fin) para usar Buscar.';

  private readonly exportFiltersIncompleteMsg =
    'Selecciona un cliente y define el rango de fechas (inicio y fin) para solicitar el reporte Excel.';

  constructor(
    private analyticsService: AnalyticsService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.loadClients();
    this.setupCascadingLogic();

    this.filterForm.valueChanges.subscribe(() => {
      if (this.canSubmitFilters) {
        this.filtersFooterAlert = null;
      }
    });
  }

  onSearch() {
    if (!this.canSubmitFilters) {
      this.filtersFooterAlert = this.searchFiltersIncompleteMsg;
      return;
    }

    this.filtersFooterAlert = null;
    this.isLoadingMetrics = true;

    const rawValue = this.filterForm.value;
    const range = rawValue.rangeDate as Date[] | null | undefined;

    const fromDate = range?.[0] ? moment(range[0]).format('YYYY-MM-DD') : '';
    const toDate = range?.[1] ? moment(range[1]).format('YYYY-MM-DD') : '';

    const filters = {
      category: rawValue.category,
      subcategory: rawValue.subcategory,
      product: rawValue.product,
      country: rawValue.country,
      fromDate: fromDate,
      toDate: toDate
    };

    console.log('Enviando filtros:', filters);

    this.analyticsService.getSuccessMetrics(filters).subscribe({
      next: (res: any) => {
        const data = res.data || {};
        this.successTotal = data.counter || 0;
        // Procesamos la gráfica
        this.processSuccessChart(data.axisX || [], data.axisY || []);
      },
      error: (err) => console.error(err)
    });

    this.analyticsService.getErrorMetrics(filters).subscribe({
      next: (res: any) => {
        const data = res.data || {};
        const errorList: ErrorItem[] = data.errorList || [];

        // Calcular total sumando los counters de 400 y 500
        this.errorTotal = errorList.reduce((sum, item) => sum + (item.counter || 0), 0);

        // Procesamos las barras unificando fechas
        this.processErrorChart(errorList);

        this.isLoadingMetrics = false;
      },
      error: (err) => {
        console.error(err);
        this.isLoadingMetrics = false;
      }
    });
  }

  // --- LÓGICA GRÁFICA SUCCESS ---
  processSuccessChart(dates: string[], values: number[]) {
    if (!dates.length || !values.length) return;

    const width = 400;
    const height = 150;
    const paddingY = 20;

    const maxVal = Math.max(...values, 1);
    const stepX = width / (dates.length - 1 || 1);

    // Generar coordenadas para TODOS los puntos
    const points = values.map((val, i) => {
      const x = i * stepX;
      const y = height - ((val / maxVal) * (height - paddingY));
      return {
        x,
        y,
        val,
        dateLabel: moment(dates[i]).format('DD MMM') // Formato corto para tooltip
      };
    });

    // 1. Crear el PATH de la Línea
    this.successPathLine = `M${points[0].x},${points[0].y} ` +
      points.slice(1).map(p => `L${p.x},${p.y}`).join(' ');

    // 2. Crear el PATH del Área
    this.successPathArea = `${this.successPathLine} L${points[points.length - 1].x},${height} L0,${height} Z`;

    // 3. Puntos destacados: ¡GUARDAMOS TODOS!
    // ELIMINAMOS EL FILTER para que coincidan con las aristas
    this.successPoints = points;

    // 4. Etiquetas X (Estas sí las filtramos para no amontonar texto)
    this.successLabels = dates
      .filter((_, i) => i % Math.ceil(dates.length / 6) === 0)
      .map(d => moment(d).format('DD MMM'));
  }

  // --- LÓGICA GRÁFICA ERROR (STACKED/GROUPED BARS) ---
  processErrorChart(errorList: ErrorItem[]) {
    // 1. Encontrar la lista maestra de fechas únicas
    const allDates = new Set<string>();
    errorList.forEach(err => err.axisX.forEach(d => allDates.add(d)));
    const sortedDates = Array.from(allDates).sort(); // Ordenar cronológicamente

    if (!sortedDates.length) return;

    // 2. Extraer datos de 400 y 500
    const err400 = errorList.find(e => e.name === 'error_400');
    const err500 = errorList.find(e => e.name === 'error_500');

    // 3. Crear mapa de valores por fecha para acceso rápido
    const getVal = (item: ErrorItem | undefined, date: string) => {
      if (!item) return 0;
      const idx = item.axisX.indexOf(date);
      return idx !== -1 ? item.axisY[idx] : 0;
    };

    // 4. Calcular escalas
    const width = 400;
    const height = 150;
    const maxValY = Math.max(
      ...sortedDates.map(d => Math.max(getVal(err400, d), getVal(err500, d))),
      1
    ); // Buscamos la barra más alta individualmente

    const barWidth = 10; // Ancho fijo o dinámico
    const stepX = width / (sortedDates.length || 1);

    // 5. Construir objetos para el HTML
    this.errorBars = sortedDates.map((date, i) => {
      const val400 = getVal(err400, date);
      const val500 = getVal(err500, date);

      const h400 = (val400 / maxValY) * (height - 20);
      const h500 = (val500 / maxValY) * (height - 20);

      return {
        x: (i * stepX) + (stepX / 2) - 10,
        h400,
        y400: height - h400,
        h500,
        y500: height - h500,

        // 🔄 CAMBIO AQUÍ: Usamos 'DD MMM' en lugar de 'ddd'
        // Antes: label: moment(date).format('ddd')
        label: moment(date).format('DD MMM'),

        raw400: val400,
        raw500: val500,
        fullDate: moment(date).format('DD MMM YYYY')
      };
    });

    // Filtramos etiquetas para no amontonar
    this.errorLabels = this.errorBars
      .filter((_, i) => i % Math.ceil(this.errorBars.length / 7) === 0)
      .map(b => b.label);
  }

  loadClients() {
    this.analyticsService.getClients().subscribe({
      next: (res: any) => this.clientsList = res.data || [],
      error: (err) => console.error(err)
    });
  }

  setupCascadingLogic() {
    this.filterForm.get('client')?.valueChanges.subscribe(clientId => {
      if (clientId) {
        this.loadCatalogs(clientId);
        this.filterForm.patchValue({ category: '', subcategory: '', country: '', product: '' }, { emitEvent: false });
      }
    });

    this.filterForm.get('category')?.valueChanges.subscribe(catId => {
      this.filterForm.patchValue({ subcategory: '', product: '' }, { emitEvent: false });

      this.filterSubcategories(catId);

      this.filterProducts();
    });

    this.filterForm.get('subcategory')?.valueChanges.subscribe(() => {
      this.filterForm.patchValue({ product: '' }, { emitEvent: false });
      this.filterProducts();
    });

    this.filterForm.get('country')?.valueChanges.subscribe(() => {
      this.filterForm.patchValue({ product: '' }, { emitEvent: false });
      this.filterProducts();
    });
  }

  loadCatalogs(clientId: string) {
    this.analyticsService.getCatalogs(clientId).subscribe({
      next: (res: any) => {
        const data = res.data || {};
        this.countriesList = data.countries || [];
        this.rawCategories = data.categories || [];
        this.rawSubcategories = data.subcategories || [];
        this.rawProducts = data.products || [];

        this.filteredCategories = this.rawCategories;
        this.filteredSubcategories = this.rawSubcategories;
        this.filteredProducts = this.rawProducts;
      },
      error: (err) => console.error(err)
    });
  }

  filterSubcategories(categoryId: string) {
    if (!categoryId) {
      this.filteredSubcategories = this.rawSubcategories;
      return;
    }

    const productsInCat = this.rawProducts.filter(p => p.categoryId === categoryId);

    const validSubcatIds = [...new Set(productsInCat.map(p => p.subcategoryId))];

    this.filteredSubcategories = this.rawSubcategories.filter(sub => validSubcatIds.includes(sub.id));
  }

  filterProducts() {
    const selectedCat = this.filterForm.get('category')?.value;
    const selectedSub = this.filterForm.get('subcategory')?.value;
    const selectedCountry = this.filterForm.get('country')?.value;

    this.filteredProducts = this.rawProducts.filter(product => {
      const matchCat = selectedCat ? product.categoryId === selectedCat : true;

      const matchSub = selectedSub ? product.subcategoryId === selectedSub : true;

      const matchCountry = selectedCountry ? product.countryId === selectedCountry : true;

      return matchCat && matchSub && matchCountry;
    });
  }
  downloadGeneralReport() {
    if (!this.canSubmitFilters) {
      this.filtersFooterAlert = this.exportFiltersIncompleteMsg;
      return;
    }

    this.filtersFooterAlert = null;

    const ref = this.dialog.open(ExportConfirmDialogComponent, {
      width: 'min(92vw, 420px)',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: '.dlg-btn--primary',
      panelClass: 'panel-b2b-export-confirm-dialog',
    });

    ref.afterClosed().subscribe((confirmed: boolean | undefined) => {
      if (!confirmed) {
        return;
      }
      this.executeExportRequest();
    });
  }

  private executeExportRequest(): void {
    this.filtersFooterAlert = null;

    const rawValue = this.filterForm.value;
    const range = rawValue.rangeDate as Date[] | null | undefined;
    const fromDate = range?.[0] ? moment(range[0]).format('YYYY-MM-DD') : '';
    const toDate = range?.[1] ? moment(range[1]).format('YYYY-MM-DD') : '';

    const params: any = {
      fromDate,
      toDate
    };

    // Solo agrega los filtros si tienen valor
    if (rawValue.category) params.category = rawValue.category;
    if (rawValue.subcategory) params.subcategory = rawValue.subcategory;
    if (rawValue.product) params.product = rawValue.product;
    if (rawValue.country) params.country = rawValue.country;

    this.isDownloadingReport = true;

    this.analyticsService.requestExportJob(params).subscribe({
      next: (response) => {
        this.isDownloadingReport = false;
        this.snackBar.open(
          response.message || 'Reporte en proceso. Puedes ver el progreso en Exportaciones.',
          'Ver reportes',
          { duration: 6000 }
        ).onAction().subscribe(() => this.router.navigate(['/exports']));
        this.router.navigate(['/exports']);
      },
      error: (err) => {
        console.error('Error solicitando reporte', err);
        this.isDownloadingReport = false;
        this.snackBar.open('Error al solicitar el reporte. Intenta nuevamente.', 'Cerrar', { duration: 4000 });
      }
    });
  }

}